import { baseApi } from "../../../app/api";
import { transformUserProfile } from "../helpers/transformUser";
import type { RawUserProfile } from "../../../types/api/customRestApi/customRestApiEndpointsTypes";

import type {
  LoginPayload,
  RegisterPayload,
  AuthResponse,
  UserProfile,
  UpdateProfilePayload,
  AuthSecurityPayload,
  ForgotPasswordPayload,
  ResetPasswordPayload,
} from "../types/userAuthApiTypes";

const CUSTOM_REST_API_BASE =
  window.commonVariables?.customRestApiBase || "/wp-json/ecommerce-app/v1";

export const userAuthApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // --- 1. Auth Mutation (Login & Register) ---
    authenticateUser: builder.mutation<
      AuthResponse,
      { type: "login" | "register" } & Partial<
        LoginPayload & RegisterPayload & AuthSecurityPayload
      >
    >({
      query: (args) => {
        const url =
          args.type === "login"
            ? `${CUSTOM_REST_API_BASE}/user/login`
            : `${CUSTOM_REST_API_BASE}/user/register`;

        return {
          url,
          method: "POST",
          body: args,
        };
      },
      // Invalidating these tags is critical. The next request for Cart/User/Wishlist
      // will use the NEW nonce provided in the authenticateUser response header/body.
      invalidatesTags: ["User", "Cart", "Wishlist", "CompareList"],
    }),

    // --- 2. User Profile ---
    getUserProfile: builder.query<UserProfile, void>({
      query: () => ({
        url: `${CUSTOM_REST_API_BASE}/user/profile`,
      }),
      transformResponse: (response: RawUserProfile) =>
        transformUserProfile(response),
      providesTags: ["User"],
    }),

    // --- 3. Update Profile Mutation ---
    updateUserProfile: builder.mutation<UserProfile, UpdateProfilePayload>({
      query: (payload) => ({
        url: `${CUSTOM_REST_API_BASE}/user/profile`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: RawUserProfile) =>
        transformUserProfile(response),
      invalidatesTags: ["User", "Cart", "Checkout"],
    }),

    // --- 4. Logout Mutation ---
    logoutUser: builder.mutation<AuthResponse, void>({
      query: () => ({
        url: `${CUSTOM_REST_API_BASE}/user/logout`,
        method: "POST",
      }),
      // Invalidate everything to clear sensitive data from cache
      invalidatesTags: ["User", "Cart", "Wishlist", "CompareList", "Order"],
    }),

    // --- 5. Forgot Password (Send Email) ---
    forgotPassword: builder.mutation<
      { message: string },
      ForgotPasswordPayload
    >({
      query: (payload) => ({
        url: `${CUSTOM_REST_API_BASE}/user/forgot-password`,
        method: "POST",
        body: payload,
      }),
    }),

    // --- 6. Reset Password (Action) ---
    resetPassword: builder.mutation<{ message: string }, ResetPasswordPayload>({
      query: (payload) => ({
        url: `${CUSTOM_REST_API_BASE}/user/reset-password`,
        method: "POST",
        body: payload,
      }),
    }),
  }),
});

export const {
  useGetUserProfileQuery,
  useAuthenticateUserMutation,
  useLogoutUserMutation,
  useUpdateUserProfileMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} = userAuthApi;
